import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { Formik, Form } from 'formik';
import { Grid, Header, Button, Message, Popup, Icon } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { useCharacterOperationsZoneBased } from '../../hooks/entities/useCharacterOperationsZoneBased';
import { useSharedData } from '../../hooks/core/useZoneHooks';
import { characterValidationSchema } from '../../../bookWorksheet/utils/validationSchemas';
import { FormikInput, FormikTextArea, FormikSelect, FormikField, FormChangeDetector } from '../../../bookWorksheet/components/common';
import { StatusForm, StatusMessage } from '../../../components/statusTools/StatusIndicators';
import AvatarPicker from '../../../components/AvatarPicker';
import { IMAGE_CONFIG } from '../../../constants/imageConfig';
import { config, characterTypeList, characterGenderList } from '../../../constants/config';
import useMediaUpload from '../../../hooks/useMediaUpload';
import useStatusManagement from '../../../hooks/bookCreator/useStatusManagement';
import '../../styles/voiceActorWorksheet.css';
import { SimpleValuesDisplay } from '../../../utils/aaiProfileTools';
import '../../../bookWorksheet/styles/bookFormEnhanced.css';

/**
 * CharacterFormEnh component
 * Enhanced version of CharacterForm with improved UI matching BookWorksheet CharacterForm-enh
 * Form for creating and editing characters in VoiceActor portfolios
 * MIRRORS BookWorksheet implementation exactly with VoiceActor entity adaptations
 * @param {Object} props - Component props
 * @param {Function} props.onSave - Callback when the form is saved
 * @returns {JSX.Element} CharacterFormEnh component
 */
const CharacterFormEnh = memo(({ onSave }) => {
  // Get data and functions from the new zone-based hooks (MIRROR BookWorksheet pattern)
  const { selectedCharacter, createCharacter, updateCharacter, charactersLoading, fetchCharacter } = useCharacterOperationsZoneBased();
  const sharedData = useSharedData();

  // Extract needed data from shared data (MIRROR BookWorksheet pattern)
  const { selections } = sharedData;
  const characterId = selections?.characterId;
  const portfolioId = selections?.portfolioId; // portfolioId instead of bookId
  const actorId = selections?.actorId; // actorId instead of litGroupId

  // Simplified local state - avatar management moved to AvatarPicker
  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formChanged, setFormChanged] = useState(false);
  const [newCharacterUuid, setNewCharacterUuid] = useState(null);
  const [avatarData, setAvatarData] = useState(null); // Store avatar data from AvatarPicker

  // NEW: Upload status state for clean AvatarPicker communication
  const [avatarUploadStatus, setAvatarUploadStatus] = useState('idle');
  const [avatarUploadResult, setAvatarUploadResult] = useState(null);

  // NEW: Dropdown z-index management
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Dynamic z-index calculation for dropdown layering
  const getDropdownZIndex = useCallback((dropdownName) => {
    if (activeDropdown === dropdownName) {
      // Priority and Status dropdowns get higher z-index when active
      if (dropdownName === 'status') {
        return 20000; // Highest priority for status form (priority & status dropdowns)
      }
      return 15000; // High for other dropdowns when active
    } else if (activeDropdown && activeDropdown !== dropdownName) {
      return 5000;  // Lower when another is active
    } else {
      // Priority and Status dropdowns get higher default z-index
      if (dropdownName === 'status') {
        return 12000; // Higher default for status form
      }
      return 10000; // Default high value for other dropdowns
    }
  }, [activeDropdown]);

  // Dropdown event handlers
  const handleDropdownOpen = useCallback((dropdownName) => {
    console.log('[VA_CHARACTER FORM] Dropdown opened:', dropdownName);
    setActiveDropdown(dropdownName);
  }, []);

  const handleDropdownClose = useCallback(() => {
    console.log('[VA_CHARACTER FORM] Dropdown closed');
    setActiveDropdown(null);
  }, []);

  const formikRef = useRef();
  const prevUuidRef = useRef(selectedCharacter?.uuid);

  // Get user profile from Redux
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.data?.userId || '00000000-0000-0000-0000-000000000000';

  // Keep useMediaUpload hook at component level for manual uploads
  const { uploadMedia } = useMediaUpload();

  // Initialize status management hook
  const statusManagement = useStatusManagement(selectedCharacter, formikRef);
  const {
    priority, setPriority,
    status, setStatus,
    deadline, setDeadline,
    priorityOptions, statusOptions,
    getPriorityColor, getStatusColor,
    isDeadlineApproaching, isDeadlinePast,
    getDeadlineWarning
  } = statusManagement;

  // Generate a new UUID for new characters
  useEffect(() => {
    if (!characterId && !newCharacterUuid) {
      // Generate a UUID for new characters
      const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
      setNewCharacterUuid(uuid);
    }
  }, [characterId, newCharacterUuid]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Reset state when character changes (RESTORED - Mirror BookWorksheet)
  useEffect(() => {
    if (selectedCharacter) {
      setPriority(selectedCharacter.priority || 'medium');
      setStatus(selectedCharacter.status || 'active');
      setDeadline(selectedCharacter.deadline || null);
    } else {
      setPriority('medium');
      setStatus('active');
      setDeadline(null);
    }

    // Clear avatar data when character changes
    // setAvatarData(null);
  }, [selectedCharacter?.uuid, selectedCharacter?.priority, selectedCharacter?.status, selectedCharacter?.deadline]);

  // Separate effect for avatar data clearing
  useEffect(() => {
    if (prevUuidRef.current !== selectedCharacter?.uuid) {
      setAvatarData(null);
      setAvatarUploadStatus('idle');
      setAvatarUploadResult(null);
      prevUuidRef.current = selectedCharacter?.uuid;
    }
  }, [selectedCharacter?.uuid]);

  // Reset upload status after successful completion
  useEffect(() => {
    if (avatarUploadStatus === 'success') {
      const timer = setTimeout(() => {
        setAvatarUploadStatus('idle');
        setAvatarUploadResult(null);
      }, 6000); // Reset after 6 seconds (1 second after the 5-second success timer)

      return () => clearTimeout(timer);
    }
  }, [avatarUploadStatus]);


  // Enhanced avatar change handler for new AvatarPicker
  const handleAvatarChange = useCallback((avatarChangeData) => {
    console.log('[VA_CHARACTER FORM] Avatar changed:', {
      ...avatarChangeData,
      filePresent: !!avatarChangeData.file,
      fileName: avatarChangeData.file?.name,
      fileSize: avatarChangeData.file?.size,
      timestamp: new Date().toISOString()
    });

    // Store avatar data for form submission
    setAvatarData(avatarChangeData);
    console.log('[VA_CHARACTER FORM] Avatar data stored in state');

    // Update Formik state to trigger form dirty detection
    if (formikRef.current) {
      // Don't set blob URLs in Formik - they're temporary and internal to AvatarPicker
      if (!avatarChangeData.url.startsWith('blob:')) {
        console.log('[VA_CHARACTER FORM] Setting non-blob URL in Formik:', avatarChangeData.url);
        formikRef.current.setFieldValue('avatarUrl', avatarChangeData.url);
      } else {
        console.log('[VA_CHARACTER FORM] Skipping blob URL in Formik, keeping for upload');
      }
      formikRef.current.setFieldTouched('avatarUrl', true);

      // Force form to recognize it's dirty
      formikRef.current.setFieldValue('_avatarChanged', Date.now());
      console.log('[VA_CHARACTER FORM] Form marked as dirty due to avatar change');
    }

    // Mark form as changed
    setFormChanged(avatarChangeData.hasChanges);
    console.log('[VA_CHARACTER FORM] Form changed state set to:', avatarChangeData.hasChanges);
  }, []);

  // Handle upload completion from AvatarPicker
  const handleUploadComplete = useCallback((uploadResult) => {
    console.log('[VA_CHARACTER FORM] Avatar upload completed:', uploadResult);

    // Update form with final uploaded URL
    if (formikRef.current) {
      formikRef.current.setFieldValue('avatarUrl', uploadResult.url);
    }
  }, []);

  // Handle upload errors from AvatarPicker
  const handleUploadError = useCallback((error) => {
    console.error('[VA_CHARACTER FORM] Avatar upload error:', error);
    setErrorMessage('Failed to upload avatar: ' + (error.message || error));
  }, []);

  // Get form values with consistent defaults
  const getFormValues = useCallback((data) => {
    return {
      name: data?.name || '',
      role: data?.role || '',
      description: data?.description || '',
      age: data?.age || '',
      gender: data?.gender || '',
      status: data?.status || 'active',
      priority: data?.priority || 'medium',
      deadline: data?.deadline || null,
      avatarUrl: data?.avatarUrl || IMAGE_CONFIG.CHARACTER_AVATAR.defaultUrl,
      avatarFile: null,
      hasNewAvatarFile: false
    };
  }, []);

  // Get initial values for the form
  const getInitialValues = () => {
    return getFormValues(selectedCharacter);
  };

  // Validate form data before submission
  const validateFormData = useCallback((values) => {
    // Check required fields
    if (!values.name || !values.name.trim()) {
      return 'Character Name is required';
    }
    if (!values.description || !values.description.trim()) {
      return 'Description is required';
    }
    return null; // No errors
  }, []);

  // Enhanced form submission handler with deduplication
  const [isSubmittingRef, setIsSubmittingRef] = useState(false);

  const handleSubmit = async (values) => {
    // Prevent duplicate submissions
    if (isSubmittingRef) {
      console.log('[VA_CHARACTER FORM] Skipping duplicate submission');
      return;
    }

    setIsSubmittingRef(true);
    setFormLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    console.log('[VA_CHARACTER FORM] Form submission started:', {
      values,
      avatarDataPresent: !!avatarData,
      avatarDataDetails: avatarData ? {
        hasFile: !!avatarData.file,
        fileName: avatarData.file?.name,
        hasChanges: avatarData.hasChanges,
        isUploaded: avatarData.isUploaded,
        isPreview: avatarData.isPreview,
        url: avatarData.url
      } : null,
      timestamp: new Date().toISOString()
    });

    // Validate form data
    const validationError = validateFormData(values);
    if (validationError) {
      console.log('[VA_CHARACTER FORM] Validation failed:', validationError);
      setErrorMessage(validationError);
      setFormLoading(false);
      setIsSubmittingRef(false); // Reset submission flag on validation error
      return;
    }

    try {
      let finalAvatarUrl = values.avatarUrl;
      console.log('[VA_CHARACTER FORM] Initial avatar URL from form values:', finalAvatarUrl);

      // Check avatar upload conditions
      const needsUpload = avatarData?.file && avatarData?.hasChanges && !avatarData?.isUploaded;
      const isPreUploaded = avatarData?.isUploaded;

      console.log('[VA_CHARACTER FORM] Avatar upload analysis:', {
        needsUpload,
        isPreUploaded,
        hasFile: !!avatarData?.file,
        hasChanges: avatarData?.hasChanges,
        isUploaded: avatarData?.isUploaded
      });

      // Handle avatar upload if AvatarPicker has new file data
      if (needsUpload) {
        try {
          console.log('[VA_CHARACTER FORM] Starting avatar upload with params:', {
            fileName: avatarData.file.name,
            fileSize: avatarData.file.size,
            resourceType: 'voiceActorCharacterAvatarImage', // VoiceActor specific resource type
            uploadParams: {
              userId: userId,
              actorId: actorId || '00000000-0000-0000-0000-000000000000', // actorId instead of litGroupId
              portfolioId: portfolioId || '00000000-0000-0000-0000-000000000000', // portfolioId instead of bookId
              characterId: characterId || newCharacterUuid,
              resourceType: 'voiceActorCharacterAvatarImage'
            }
          });

          const uploadUrl = await uploadMedia(
            avatarData.file,
            'voiceActorCharacterAvatarImage', // VoiceActor specific resource type
            {
              userId: userId,
              actorId: actorId || '00000000-0000-0000-0000-000000000000', // actorId instead of litGroupId
              portfolioId: portfolioId || '00000000-0000-0000-0000-000000000000', // portfolioId instead of bookId
              characterId: characterId || newCharacterUuid,
              resourceType: 'voiceActorCharacterAvatarImage'
            },
            userId
          );

          console.log('[VA_CHARACTER FORM] Avatar uploaded successfully:', uploadUrl);
          finalAvatarUrl = uploadUrl;

          // NEW: Signal upload success to AvatarPicker via props
          setAvatarUploadStatus('success');
          setAvatarUploadResult({
            url: uploadUrl,
            timestamp: new Date(),
            originalFile: avatarData.file,
            metadata: {
              userId: userId,
              actorId: actorId || '00000000-0000-0000-0000-000000000000',
              portfolioId: portfolioId || '00000000-0000-0000-0000-000000000000',
              characterId: characterId || newCharacterUuid,
              resourceType: 'voiceActorCharacterAvatarImage'
            }
          });
          console.log('[VA_CHARACTER FORM] Avatar upload success signaled to AvatarPicker');
        } catch (err) {
          console.error('[VA_CHARACTER FORM] Avatar upload failed:', err);
          setErrorMessage('Failed to upload avatar: ' + err.message);
          setFormLoading(false);
          return;
        }
      } else if (isPreUploaded) {
        // Avatar was already uploaded by AvatarPicker (auto-upload mode)
        finalAvatarUrl = avatarData.url;
        console.log('[VA_CHARACTER FORM] Using pre-uploaded avatar URL:', finalAvatarUrl);
      } else {
        console.log('[VA_CHARACTER FORM] No avatar upload needed, using existing URL:', finalAvatarUrl);
      }

      // Prepare character data for API
      const characterData = {
        characterId: characterId,
        name: values.name,
        description: values.description,
        role: values.role,
        age: values.age,
        gender: values.gender,
        priority: priority || 'medium',
        status: status || 'active',
        deadline: deadline || null,
        avatarUrl: finalAvatarUrl,
        uuid: characterId || newCharacterUuid
      };

      console.log('[VA_CHARACTER FORM] Sending character data to API:', characterData);

      let result;

      if (characterId) {
        // result = await updateCharacter({ characterData });

        // Update existing character
        result = await updateCharacter({
          characterId,
          portfolioId,
          data: characterData
        }); // portfolioId instead of bookId
        setSuccessMessage('Character updated successfully!');
      } else if (portfolioId) {
        // Create new character
        result = await createCharacter({
          characterId,
          portfolioId,
          data: characterData
        }); // portfolioId instead of bookId
        setSuccessMessage('Character created successfully!');
      } else {
        throw new Error('No portfolio selected');
      }

      // Reset form state
      setFormChanged(false);
      setAvatarData(null);

      // Call the onSave callback if provided
      if (onSave) {
        onSave(result);
      }

      // Fetch the updated character data
      if (result && result.uuid) {
        fetchCharacter(result.uuid, portfolioId); // portfolioId instead of bookId
      }
    } catch (err) {
      console.error('[VA_CHARACTER FORM] Form submission failed:', err);
      setErrorMessage(err.message || 'An error occurred while saving the character');
    } finally {
      setFormLoading(false);
      setIsSubmittingRef(false); // Reset submission flag
    }
  };

  // Reset form to initial values with consistent approach (RESTORED - Mirror BookWorksheet)
  const formReset = useCallback((resetData) => {
    if (formikRef.current) {
      console.log('[VA_CHARACTER FORM] Resetting form with data:', resetData);

      formikRef.current.resetForm({
        values: getFormValues(resetData)
      });

      // Reset status management state (RESTORED - Mirror BookWorksheet)
      setPriority(resetData?.priority || 'medium');
      setStatus(resetData?.status || 'active');
      setDeadline(resetData?.deadline || null);

      // Reset avatar data
      setAvatarData(null);

      // Reset form changed state
      setFormChanged(false);
    }
  }, [getFormValues, setPriority, setStatus, setDeadline]);

  // Show message if no portfolio is selected
  if (!portfolioId) {
    return (
      <Message info>
        <Message.Header>No Portfolio Selected</Message.Header>
        <p>Please select a portfolio first to create or edit a character.</p>
      </Message>
    );
  }

  return (
    <div className="va-form-container noPad">
      <Header as="h3" dividing>
        {characterId ? 'Edit Character' : 'Create New Character'}
      </Header>

      <Formik
        innerRef={formikRef}
        initialValues={getInitialValues()}
        validationSchema={characterValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, values, isValid, dirty, errors, touched }) => (
          <Form>
            <FormChangeDetector
              onChange={setFormChanged}
              initialValues={getInitialValues()}
            />
            <div style={{ position: "absolute", top: "45px", right: "10px", width: "260px", zIndex: "5000" }}>
              <Grid style={{ borderLeft: "1px solid #ECECEC" }}>
                <Grid.Column className='noPad' width={16}>
                  <AvatarPicker
                    characterId={selectedCharacter?.uuid || 'new'}
                    currentAvatarUrl={selectedCharacter?.avatarUrl || IMAGE_CONFIG.CHARACTER_AVATAR.defaultUrl}
                    onAvatarChange={handleAvatarChange}
                    onUploadComplete={handleUploadComplete}
                    onUploadError={handleUploadError}
                    resourceType="voiceActorCharacterAvatarImage" // VoiceActor specific resource type
                    uploadParams={{
                      userId,
                      actorId: actorId || '00000000-0000-0000-0000-000000000000', // actorId instead of litGroupId
                      portfolioId: portfolioId || '00000000-0000-0000-0000-000000000000', // portfolioId instead of bookId
                      characterId: characterId || newCharacterUuid
                    }}
                    showHints={true}
                    autoUpload={false}
                    uploadStatus={avatarUploadStatus}
                    uploadResult={avatarUploadResult}
                    isLoading={false}
                  />
                </Grid.Column>
              </Grid>
            </div>

            <Grid divided={false}>
              <Grid.Row>
                <Grid.Column width={8} style={{ height: "130px" }}>
                  <FormikField
                    name="name"
                    component={FormikInput}
                    label="Character Name"
                    placeholder="Enter character name"
                    fluid
                    value={values.name}
                  />
                </Grid.Column>
                <Grid.Column width={16}>
                  <FormikField
                    name="description"
                    component={FormikTextArea}
                    label="Description"
                    placeholder="Enter character description"
                    rows={3}
                    style={{ marginTop: '-5px', width: '100%' }}
                    value={values.description}
                  />

                  {/* Status form */}
                  <div style={{ marginTop: '20px' }}>
                    <Header as="h4">Status Information</Header>
                    <div style={{ position: 'relative', zIndex: getDropdownZIndex('status') }}>
                      <StatusForm
                        charName={values.name}
                        priority={priority}
                        setPriority={setPriority}
                        status={status}
                        setStatus={setStatus}
                        deadline={deadline}
                        setDeadline={setDeadline}
                        priorityOptions={priorityOptions}
                        statusOptions={statusOptions}
                        onDropdownOpen={() => handleDropdownOpen('status')}
                        onDropdownClose={handleDropdownClose}
                      />
                      <div style={{ display: 'block', position: 'absolute', bottom: '20px', left: '170px', width: "290px !important", backgroundColor: "#FF" }}>
                        <StatusMessage
                          status={status}
                          deadline={deadline}
                          isDeadlineApproaching={isDeadlineApproaching}
                          isDeadlinePast={isDeadlinePast}
                          getDeadlineWarning={getDeadlineWarning}
                          getStatusColor={getStatusColor}
                        />
                      </div>
                    </div>
                  </div>

                  <Grid>
                    <Grid.Row columns={3}>
                      <Grid.Column>
                        <FormikField
                          name="role"
                          component={FormikInput}
                          label="Role"
                          placeholder="Enter character role"
                          fluid
                          value={values.role}
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <div style={{
                          position: 'relative',
                          zIndex: getDropdownZIndex('age')
                        }}>
                          <FormikField
                            name="age"
                            component={FormikSelect}
                            label="Age"
                            options={characterTypeList}
                            placeholder="Select age"
                            fluid
                            value={values.age}
                            onOpen={() => handleDropdownOpen('age')}
                            onClose={handleDropdownClose}
                          />
                        </div>
                      </Grid.Column>
                      <Grid.Column>
                        <div style={{
                          position: 'relative',
                          zIndex: getDropdownZIndex('gender')
                        }}>
                          <FormikField
                            name="gender"
                            component={FormikSelect}
                            label="Gender"
                            options={characterGenderList}
                            placeholder="Select gender"
                            fluid
                            value={values.gender}
                            onOpen={() => handleDropdownOpen('gender')}
                            onClose={handleDropdownClose}
                          />
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Grid.Column>
              </Grid.Row>

              {/* Status messages - Avatar status now handled by AvatarPicker */}
              {successMessage && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Message positive>
                      <Message.Header>Success</Message.Header>
                      <p>{successMessage}</p>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
              )}

              {errorMessage && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Message negative>
                      <Message.Header>Error</Message.Header>
                      <p>{errorMessage}</p>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
              )}

              {/* Form buttons */}
              <Grid.Row>
                <Grid.Column width={16}>
                  <Popup
                    className='values-display-popup'
                    trigger={
                      <span>
                        <Button
                          primary
                          type="submit"
                          loading={formLoading || charactersLoading === 'loading'}
                          disabled={!formChanged}
                          onClick={() => {
                            console.log('Submit button clicked, form state:', {
                              isSubmitting,
                              isValid,
                              dirty,
                              formChanged
                            });
                            handleSubmit(formikRef.current.values);
                          }}
                        >
                          {characterId ? 'Update Character' : 'Create Character'}
                        </Button>
                      </span>
                    }
                    content={
                      <SimpleValuesDisplay
                        key={Math.random().toString()}
                        values={values}
                        errors={errors}
                        touched={touched}
                        isFormChanged={false} />
                    }
                    position="top left"
                    size="tiny"
                    offset={[0, 10]}
                  />

                  {formChanged && (
                    <Button
                      type="button"
                      onClick={() => formReset(selectedCharacter)}
                    >
                      Cancel Changes
                    </Button>
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
});

export default CharacterFormEnh;
